import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import { useT, useLang, t as translate } from '../../lib/i18n.jsx';
import { get, post } from '../../lib/api.js';
import { useAsync } from '../../lib/hooks.js';
import { toast } from '../../lib/toast.jsx';
import Modal, { Field } from '../../components/Modal.jsx';
import { errorMessage } from '../../lib/errors.js';
import { formatNumber } from '../../lib/format.js';
import { activateMail, activateMailError, mailDomainCandidates } from './mailDomains.js';

export default function EmailList() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const { data: accounts, loading, error, reload } = useAsync(() => get('/emails/accounts'), []);
  const { data: domains, reload: reloadDomains } = useAsync(() => get('/emails/domains').catch(() => []), []);
  const [newOpen, setNewOpen] = useState(params.get('action') === 'newEntry');
  const [domainOpen, setDomainOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('by_domain');

  const list = accounts || [];
  const filtered = list.filter((m) => {
    if (filter === 'active' && m.status !== 'active') return false;
    if (query && !m.email.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const domainCount = (domains || []).length;

  const totalQuota = list.reduce((a, m) => a + (m.quota_mb || 0), 0);

  const grouped = useMemo(() => {
    if (group === 'flat') return [{ domain: null, items: filtered }];
    const map = new Map();
    filtered.forEach((m) => {
      const dom = m.email.split('@')[1] || '—';
      if (!map.has(dom)) map.set(dom, []);
      map.get(dom).push(m);
    });
    return Array.from(map.entries()).map(([domain, items]) => ({ domain, items }));
  }, [filtered, group]);

  return (
    <main className="page" data-screen-label="Email">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('em.title')}</h1>
          <p className="page-sub">{t('em.sub')}</p>
        </div>
        <div className="actions">
          <button className="btn-ghost" onClick={() => setDomainOpen(true)}>
            {t('mailDomain.add')}
          </button>
          <button className="btn-primary" onClick={() => setNewOpen(true)}>
            <Icon name="plus" size={15} stroke={2} />
            <span style={{ marginLeft: 6 }}>{t('em.new')}</span>
          </button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid--4">
        <Kpi
          label={t('em.kpi.mailboxes')}
          value={list.length}
          sub={t(domainCount === 1 ? 'emailList.domains.one' : 'emailList.domains.other', { n: domainCount })}
          icon="mail"
        />
        <Kpi
          label={t('em.kpi.storage')}
          value={`${formatNumber(totalQuota / 1024, lang, { maximumFractionDigits: 1 })} GB`}
          sub={t('emailList.kpi.allocated')}
          icon="archive"
        />
        <Kpi label={t('em.kpi.spam')} value="—" sub={t('emailList.kpi.noMetric')} icon="shield" />
        <Kpi label={t('em.kpi.delivered')} value="—" sub={t('emailList.kpi.noMetric')} icon="check" />
      </div>

      <div className="filterbar">
        <div className="search-input">
          <Icon name="search" size={15} />
          <input
            placeholder={t('emailList.search')}
            aria-label={t('emailList.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="chips">
          <button className="chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            {t('em.filter.all')}
            <span style={{ marginLeft: 6, color: filter === 'all' ? 'rgba(255,255,255,.55)' : 'var(--ink-4)' }}>{list.length}</span>
          </button>
          <button className="chip" aria-pressed={filter === 'active'} onClick={() => setFilter('active')}>
            {t('em.status.active')}
            <span style={{ marginLeft: 6, color: filter === 'active' ? 'rgba(255,255,255,.55)' : 'var(--ink-4)' }}>
              {list.filter((m) => m.status === 'active').length}
            </span>
          </button>
        </div>
        <div className="seg seg--sm" style={{ marginLeft: 'auto' }} role="group">
          <button aria-pressed={group === 'by_domain'} onClick={() => setGroup('by_domain')}>
            {t('emailList.group.byDomain')}
          </button>
          <button aria-pressed={group === 'flat'} onClick={() => setGroup('flat')}>
            {t('emailList.group.flat')}
          </button>
        </div>
      </div>

      {loading && (
        <div className="state-card">{t('common.loading')}</div>
      )}

      {!loading && error && (
        <div className="state-card" role="alert">
          <h3>{t('emailList.loadError')}</h3>
          <p>{errorMessage(error, { lang })}</p>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="state-card">
          <h3>{list.length === 0 ? t('emailList.empty.title') : t('emailList.noResults.title')}</h3>
          <p>{list.length === 0 ? t('emailList.empty.body') : t('emailList.noResults.body')}</p>
        </div>
      )}

      {filtered.length > 0 && grouped.map((g, gi) => (
        <section className="card" style={{ padding: 0, marginBottom: 14 }} key={gi}>
          {g.domain && (
            <div className="mail-group-head">
              <span className="mono">{g.domain}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {t(g.items.length === 1 ? 'emailList.addresses.one' : 'emailList.addresses.other', { n: g.items.length })}
              </span>
            </div>
          )}
          <div className="mail-head">
            <span>{t('em.col.address')}</span>
            <span>{t('em.col.type')}</span>
            <span>{t('em.col.usage')}</span>
            <span>{t('em.col.last')}</span>
            <span>{t('em.col.status')}</span>
            <span />
          </div>
          {g.items.map((m) => (
            <div
              className="mail-row"
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/e-mail/${m.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/e-mail/${m.id}`); }}
            >
              <div className="mail-row__addr">
                <span className="mail-row__icon is-mailbox">
                  <Icon name="mail" size={13} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="mono" style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.email}
                  </div>
                  {m.forward_to && (
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
                      → <span className="mono">{m.forward_to}</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {t('em.type.mailbox')}
              </div>
              <div>
                <MbQuota quotaMb={m.quota_mb || 0} lang={lang} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>—</div>
              <div>
                <span className={'status-pill ' + (m.status === 'active' ? '' : 'is-warn')}>
                  <span className="dot" />
                  {mailboxStatusLabel(m.status, lang)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Icon name="arrow-right" size={14} />
              </div>
            </div>
          ))}
        </section>
      ))}

      {newOpen && (
        <NewMailboxModal
          lang={lang}
          domains={domains || []}
          onClose={() => {
            setNewOpen(false);
            if (params.get('action')) {
              params.delete('action');
              setParams(params, { replace: true });
            }
          }}
          onCreated={() => {
            setNewOpen(false);
            reload();
          }}
          onAddDomain={() => {
            setNewOpen(false);
            setDomainOpen(true);
          }}
        />
      )}

      {domainOpen && (
        <NewMailDomainModal
          lang={lang}
          onClose={() => setDomainOpen(false)}
          onCreated={() => {
            setDomainOpen(false);
            reloadDomains();
          }}
        />
      )}
    </main>
  );
}

function Kpi({ label, value, sub, icon, progress }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__top">
        <span className="kpi-card__icon"><Icon name={icon} size={16} /></span>
        <span className="kpi-card__label">{label}</span>
      </div>
      <div className="kpi-card__value">{value}</div>
      <div className="kpi-card__sub">{sub}</div>
      {progress !== undefined && (
        <div className="bar kpi-card__bar">
          <i style={{ width: Math.round(progress * 100) + '%' }} />
        </div>
      )}
    </div>
  );
}

// The API reports the quota but not the space used, so only the quota is shown.
function MbQuota({ quotaMb, lang }) {
  if (!quotaMb) return <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>;
  return (
    <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
      {formatNumber(quotaMb / 1024, lang, { maximumFractionDigits: 1 })} GB
    </span>
  );
}

const MAILBOX_STATUS_KEYS = {
  active: 'em.status.active',
  suspended: 'emailList.status.suspended',
  pending: 'emailList.status.pending',
  disabled: 'emailList.status.disabled',
};

function mailboxStatusLabel(s, lang) {
  return MAILBOX_STATUS_KEYS[s] ? translate(lang, MAILBOX_STATUS_KEYS[s]) : s || '—';
}

const LOCAL_PART = /^[a-z0-9](?:[a-z0-9._%+-]*[a-z0-9])?$/i;

function NewMailDomainModal({ lang, onClose, onCreated }) {
  const t = useT();
  const { data: candidates, loading } = useAsync(() => mailDomainCandidates(), []);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const list = candidates || [];
  const chosen = list.find((c) => c.name === name) || (list.length === 1 ? list[0] : null);

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) return;
    setErr('');
    setBusy(true);
    try {
      await activateMail(chosen.name, chosen.hostingId);
      toast(t('mailDomain.activated', { domain: chosen.name }));
      onCreated?.();
    } catch (e2) {
      setErr(activateMailError(e2, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={520}
      title={t('mailDomain.add')}
      description={t('mailDomain.description')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy || !chosen}>
            {busy ? t('emailDetail.working') : t('mailDomain.activate')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div className="login-form">
          <Field
            label={t('mod.nm.domain')}
            hint={!loading && list.length === 0 ? t('mailDomain.none') : undefined}
          >
            <select required value={chosen?.name || ''} onChange={(e) => setName(e.target.value)} disabled={loading || list.length === 0}>
              <option value="">{loading ? t('common.loading') : t('emailList.new.chooseDomain')}</option>
              {list.map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function NewMailboxModal({ lang, domains, onClose, onCreated, onAddDomain }) {
  const t = useT();
  const [domainId, setDomainId] = useState(domains.length === 1 ? String(domains[0].id) : '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [quota, setQuota] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const domain = domains.find((d) => String(d.id) === String(domainId));
  const local = username.trim().toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    if (!LOCAL_PART.test(local)) {
      setErr(t('emailList.new.localError'));
      return;
    }
    setErr('');
    setBusy(true);
    try {
      await post('/emails/accounts', {
        email_domain_id: Number(domainId),
        username: local,
        password,
        quota_mb: Number(quota),
      });
      toast(t('tst.mb_created'));
      onCreated?.();
    } catch (e2) {
      setErr(errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={520}
      title={t('mod.nm.cta')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy || domains.length === 0}>
            {busy ? t('emailDetail.working') : t('emailDetail.create')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div className="login-form">
          {domains.length === 0 && (
            <p className="field-hint" style={{ margin: 0 }}>
              {t('mailDomain.firstActivate')}{' '}
              <button type="button" className="link-btn" onClick={onAddDomain}>{t('mailDomain.add')}</button>
            </p>
          )}
          <Field label={t('mod.nm.domain')}>
            <select required value={domainId} onChange={(e) => setDomainId(e.target.value)}>
              <option value="">{t('emailList.new.chooseDomain')}</option>
              {domains.map((d) => (
                <option key={d.id} value={d.id}>{d.domain}</option>
              ))}
            </select>
          </Field>
          <Field
            label={t('emailList.new.username')}
            hint={local && domain ? `${local}@${domain.domain}` : undefined}
          >
            <input
              required
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
          <Field label={t('mod.nm.pwd')}>
            <input required type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label={t('emailList.new.quota')}>
            <input type="number" min={100} step={100} value={quota} onChange={(e) => setQuota(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
